import React from 'react';
import styled from '@emotion/styled';
import { byCard } from 'utils';
import {
  PRODUCT_CODE_AFS,
  PRODUCT_CODE_AU_GEM,
  PRODUCT_CODE_GO,
  PRODUCT_CODE_LOW_RATE,
  PRODUCT_CODE_TWENTY_EIGHT_DEGREES,
} from '_config';
import { Grid, Container, Text } from 'basis';

export function Left({ rowIndexMd = 0, rowIndexSm = 0, children }) {
  return (
    <Grid.Item colSpan="all" colSpan-md="0-1" rowSpan={rowIndexSm} rowSpan-md={rowIndexMd} margin-md="0">
      <Container padding-md="0 4 0 0" padding-lg="0 11 0 0">
        <Text textStyle="heading4" as="h4">
          {children}
        </Text>
      </Container>
    </Grid.Item>
  );
}

export function Right({ rowIndexMd = 0, rowIndexSm = 0, children }) {
  return (
    <Grid.Item colSpan="all" colSpan-md="2-7" colSpan-lg="2-5" rowSpan={rowIndexSm} rowSpan-md={rowIndexMd}>
      {children}
    </Grid.Item>
  );
}

export function Split({ children }) {
  return (
    <Grid colsGutter="0" cols="6" rowsGap="4" rowsGap-md="10">
      {children}
    </Grid>
  );
}

export function ResponsiveList({ list, formData, keyPrefix }) {
  const filteredList = list.reduce((acc, item) => {
    if (!item.condition) {
      acc.push(item);
    } else if (item.condition(formData)) {
      acc.push(item);
    }
    return acc;
  }, []);
  const { result } = filteredList.reduce(
    (acc, item, index) => {
      if (item.left) {
        acc.result.push(
          <Left rowIndexMd={index} rowIndexSm={acc.count} key={`${keyPrefix}-left-${index}`}>
            {item.left}
          </Left>,
        );
        acc.count += 1;
      }
      if (item.right) {
        acc.result.push(
          <Right rowIndexMd={index} rowIndexSm={acc.count} key={`${keyPrefix}-right-${index}`}>
            {item.right}
          </Right>,
        );
        acc.count += 1;
      }
      return acc;
    },
    { result: [], count: 0 },
  );
  return <Split>{result}</Split>;
}

export const IconTitleWrapper = styled.div`
  display: flex;
  margin-top: 14px;
  margin-bottom: 32px;
  & > img {
    margin-right: 8px;
  }

  ${props => `
    ${`@media (min-width: ${props.theme.screenSize.sm})`} {
      font-size: 18px;
    }
  `}
`;

export const ButtonWrapper = styled.div`
  border-color: var(--Flat-colours-Indigo, var(--Indigo-1000, #0b2374));

  && button:hover {
    background-color: var(--Background-Secondary-hover, #0b2374);
    & span {
      color: white;
    }
  }

  & span {
    color: var(--Flat-colours-Indigo, var(--Indigo-1000, #0b2374));
    font-family: 'Latitude Sans';
    font-weight: 600;
  }
`;

export const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 48px;
  padding-bottom: 50px;

  li::before {
    width: 0.3em;
    height: 0.3em;
  }

  li:not(:first-of-type) {
    margin-top: 12px;
  }

  a {
    cursor: pointer;
  }

  h6 {
    font-weight: 500;
  }

  ${props => `
    ${`@media (min-width: ${props.theme.screenSize.sm})`} {
      & > div {
        width: 100%;
        flex-direction: column;
      }
    }
    ${`@media (min-width: ${props.theme.screenSize.lg})`} {
      & > div:first-of-type {
        flex-direction: column;
      }

      & > div {
        max-width: ${props.theme.maxWidth || '1220px'};
        display: flex;
        flex-direction: column;
      }
    }

    ${`@media (max-width: ${props.theme.screenSize.md})`} {
    margin-top: 32px;
      h4 {
        font-size: 20px;
      }
      h6 {
        font-size: 16px;
      }
      padding-bottom: 30px;
    }
  `}
`;

export const LandingContentItem = styled.div`
  display: flex;
  img {
    margin-right: 10px;
    width: 24px;
    height: 24px;
  }
  svg {
    width: 24px;
    height: 24px;
    margin-left: -6px;
    margin-right: 4px;
  }
  ul {
    margin-top: 2px;
    margin-left: 1.5em;
  }
  margin-bottom: 20px;
  ${props => `
    ${`@media (max-width: ${props.theme.screenSize.md})`} {
      margin-bottom: 12px;
    }
  `}
`;

export const AnchorTag = styled.a`
  font-size: 8px;
  vertical-align: top;
  text-decoration: underline;
  cursor: pointer;
  ${props => `
    color: ${props.theme.a.color};
  `};
`;

export const BannerTitleContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 16px;
`;

export const ImageIcons = styled.img`
  height: 68px;
  margin-right: 16px;
  ${props => `
      ${`@media (min-width: ${props.theme.screenSize.lg})`} {
        height: 68px;
      }
  `};
`;

export const HeaderBannerTextStyle = styled.div`
  ${props => `
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    vertical-align: text-top;

    ${`@media (max-width: ${props.theme.screenSize.md})`} {
      line-height: 44px;
    }

    button {
      border-color: inherit;
      border-radius: 4px;
      padding: 12px 16px;
      height: 52px;
      font-size: 16px;
      line-height: 28px;
      color: ${byCard({
        [PRODUCT_CODE_AFS]: props.theme.primaryButtonColor,
        default: '#FFF',
      })};
    }

    sup {
      vertical-align: top;
      position: relative;
      top: -0.2em;
    }

    p {
      white-space: pre-wrap;
    }
  `}
`;

export const ProductNameWrapper = styled.div`
  ${props => `
    color: ${byCard({
      [PRODUCT_CODE_AFS]: '#000',
      [PRODUCT_CODE_AU_GEM]: 'var(--Flat-colours-Indigo, var(--Indigo-1000, #0B2374))',
      [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: 'var(--Flat-colours-Indigo, var(--Indigo-1000, #0B2374))',
      [PRODUCT_CODE_GO]: 'var(--Flat-colours-Indigo, var(--Indigo-1000, #0B2374))',
      [PRODUCT_CODE_LOW_RATE]: 'var(--Flat-colours-Indigo, var(--Indigo-1000, #0B2374))',
      default: '#FFF',
    })};
    font-size: 40px;
    font-weight: 500;
    font-family: 'Latitude Sans';
    white-space: pre-wrap;
    margin: ${byCard({
      [PRODUCT_CODE_AFS]: '40px 0 40px 0',
      default: '0 0 20px 0',
    })};


    ${`@media (max-width: ${props.theme.screenSize.md})`} {
      font-size: 32px;
      line-height: 40px;
      margin: 20px 0 0;
    }


    ${`@media (max-width: ${props.theme.screenSize.sm})`} {
      font-size: 30px;
      line-height: 40px;
      margin: 20px 0 0;
    }
  `}
`;

export const ProductSubtitle = styled.div`
  & p {
    font-family: 'Latitude Sans';
    font-size: var(--Unit-20, 20px);
    font-weight: 500;
    color: var(--Flat-colours-Indigo, var(--Indigo-1000, #0b2374));
  }
`;

export const Banner = styled.div`
  display: grid;
  align-items: center;
  & > div {
    display: flex;
    justify-content: space-between;
    grid-column: 2/3;
  }

  ${props => `
    ${`@media (max-width: ${props.theme.screenSize.sm})`} {
      & > div {
        width: 100%;
        flex-direction: column-reverse;
      }
    }
    ${`@media (min-width: ${props.theme.screenSize.lg})`} {
      grid-template-columns: 1fr max(1220px) 1fr;

      & > div {
        max-width: 1220px;
      }
    }
  `}
`;

export const BannerLeft = styled.div`
  position: relative;
  height: ${byCard({
    [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: '352.294px',
    [PRODUCT_CODE_LOW_RATE]: '358.294px',
    default: '329.294px;',
  })};
  width: ${byCard({
    [PRODUCT_CODE_AU_GEM]: '41%',
    [PRODUCT_CODE_GO]: '45%',
    [PRODUCT_CODE_LOW_RATE]: '41%',
    [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: '48%',
    default: '51%',
  })};

  & > img:first-child {
    position: absolute;
    width: ${byCard({
      [PRODUCT_CODE_AU_GEM]: '504px',
      [PRODUCT_CODE_GO]: '494px',
      [PRODUCT_CODE_LOW_RATE]: '560px',
      [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: '562px',
      default: '612px',
    })};
    z-index: -1;
    right: ${byCard({
      [PRODUCT_CODE_AU_GEM]: '-94px',
      [PRODUCT_CODE_GO]: '-128px',
      [PRODUCT_CODE_LOW_RATE]: '-129px',
      [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: '-156px',
      default: '',
    })};
  }

  ${props => `
    ${`@media (max-width: ${props.theme.screenSize.lg})`} {
        display: none;
    }
    `}
`;

export const BannerRight = styled.div`
  background: ${byCard({
    [PRODUCT_CODE_AFS]: '#F5F5F7',
    [PRODUCT_CODE_AU_GEM]: 'var(--Gradients-Breeze-Blue, linear-gradient(180deg, #A4D6FF 0%, #F1F9FF 100%))',
    [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]:
      'var(--Gradients-Breeze-Blue, linear-gradient(180deg, #A4D6FF 0%, #F1F9FF 100%))',
    [PRODUCT_CODE_GO]: 'var(--Gradients-Breeze-Blue, linear-gradient(180deg, #A4D6FF 0%, #F1F9FF 100%))',
    [PRODUCT_CODE_LOW_RATE]: 'var(--Gradients-Breeze-Blue, linear-gradient(180deg, #A4D6FF 0%, #F1F9FF 100%))',
    default: 'linear-gradient(129deg, #0032aa 0%, #0062ed 100%)',
  })};
  border-radius: 0 0 0 var(--space-32, 128px);
  display: flex;
  padding: ${byCard({
    [PRODUCT_CODE_LOW_RATE]: '0 54px',
    default: '0 50px;',
  })};

  & > div {
    margin-top: 10px;
  }

  ${props => `
    ${`@media (max-width: 992px)`} {
        border-radius: 0 0 0 75px;
    }
    `}

  ${props => `
    ${`@media (max-width: ${props.theme.screenSize.sm})`} {
        position: relative;
        flex-direction: column;
        padding: 2px 20px;
        margin-bottom: ${byCard({
          [PRODUCT_CODE_AU_GEM]: '60px',
          [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: '90px',
          [PRODUCT_CODE_GO]: '60px',
          [PRODUCT_CODE_LOW_RATE]: '90px',
          default: '60px',
        })}

    }
    `}
`;

export const BannerImageContainer = styled.div`
  display: flex;
  align-items: center;
  img {
    display: block;
    width: auto;
    height: 213px;
    margin: auto;
    margin-left: ${byCard({
      [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: '24px',
      default: '',
    })}
  };
  ${props => `
    ${`@media (max-width: ${props.theme.screenSize.lg})`} {
    margin: 10px;
        img {
            width: auto;
            height: 250px;
            margin-top: 20px;
            margin-bottom: 10px;
        }
    }
  `}

 ${props => `
    ${`@media (max-width: 820px)`} {
    margin: 10px;
        img {
            width: auto;
            height: 180px;
            margin-top: 20px;
            margin-bottom: 10px;
            margin-left: ${byCard({
              [PRODUCT_CODE_AU_GEM]: '11px',
              [PRODUCT_CODE_LOW_RATE]: '7px',
              [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: '33px',
              default: '',
            })}
        }
    }
  `}


  ${props => `
    ${`@media (max-width: ${props.theme.screenSize.sm})`} {

        img {
            position: absolute;
            width: 95.098px;
            height: 150px;
            top: ${byCard({
              [PRODUCT_CODE_AU_GEM]: '170px',
              [PRODUCT_CODE_LOW_RATE]: '140px',
              [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: '170px',
              [PRODUCT_CODE_GO]: '200px',
              default: '300px',
            })};
            right: 40px;

        }
    }
  `}
  ${props => `
    ${`@media (max-width: 457px)`} {

        img {
            position: absolute;
            width: 95.098px;
            height: 150px;
            top: ${byCard({
              [PRODUCT_CODE_AU_GEM]: '170px',
              [PRODUCT_CODE_LOW_RATE]: '140px',
              [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: '170px',
              [PRODUCT_CODE_GO]: '220px',
              default: '300px',
            })};
            right: 40px;

        }
    }
  `}

  ${props => `
    ${`@media (max-width: 398px)`} {

        img {
            position: absolute;
            width: 95.098px;
            height: 150px;
            top: ${byCard({
              [PRODUCT_CODE_AU_GEM]: '190px',
              [PRODUCT_CODE_LOW_RATE]: '170px',
              [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: '190px',
              [PRODUCT_CODE_GO]: '260px',
              default: '300px',
            })};
            right: 40px;

        }
    }
  `}

  ${props => `
    ${`@media (max-width: ${props.theme.screenSize.xs})`} {

        img {
            position: absolute;
            width: 95.098px;
            height: 150px;
            top: ${byCard({
              [PRODUCT_CODE_AU_GEM]: '210px',
              [PRODUCT_CODE_LOW_RATE]: '170px',
              [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: '210px',
              [PRODUCT_CODE_GO]: '264px',
              default: '300px',
            })};
            right: 17px;

        }
    }
  `}
`;

export const BannerImage = ({ children }) => {
  return <BannerImageContainer>{children}</BannerImageContainer>;
};

export const OverviewBox = styled.div`
  ${props => `
    ${`@media (min-width: ${props.theme.screenSize.sm})`} {
      border: 12px solid ${props.theme?.colors?.grey?.t03 ?? '#F8F8F8'};
      padding: 24px;
    }`}
`;
